import { Wifi, WifiOff, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type ConnectionStatus = 'connected' | 'disconnected' | 'connecting'

interface ConnectionIndicatorProps {
  isConnected: boolean
  isReconnecting?: boolean
  className?: string
}

export function ConnectionIndicator({
  isConnected,
  isReconnecting = false,
  className
}: ConnectionIndicatorProps) {
  const status: ConnectionStatus = isReconnecting
    ? 'connecting'
    : isConnected
      ? 'connected'
      : 'disconnected'

  const statusConfig = {
    connected: {
      icon: Wifi,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
      pulseColor: 'bg-emerald-500',
      label: 'Conectado',
      description: 'Conexão estável com o servidor'
    },
    disconnected: {
      icon: WifiOff,
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
      pulseColor: 'bg-rose-500',
      label: 'Desconectado',
      description: 'Sem conexão com o servidor'
    },
    connecting: {
      icon: Loader2,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      pulseColor: 'bg-amber-500',
      label: 'Reconectando',
      description: 'Tentando reconectar...'
    }
  }

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors",
              config.bgColor,
              className
            )}
          >
            {/* Dot indicator */}
            <span className="relative flex h-2 w-2">
              {status === 'connected' && (
                <span
                  className={cn(
                    "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                    config.pulseColor
                  )}
                />
              )}
              <span
                className={cn(
                  "relative inline-flex rounded-full h-2 w-2",
                  config.pulseColor
                )}
              />
            </span>

            {/* Icon */}
            <Icon
              className={cn(
                "h-3.5 w-3.5",
                config.color,
                status === 'connecting' && "animate-spin"
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="bg-game-secondary dark:bg-zinc-800 border-game-border-primary dark:border-zinc-700"
        >
          <div className="text-center">
            <p className={cn("font-medium", config.color)}>{config.label}</p>
            <p className="text-xs text-game-muted dark:text-zinc-400">{config.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
